/** Visible baby-name tool copy per page language (/baby-names vs /hi/baby-names). */
export type BabyNamesLanguage = "en" | "hi";

export const BABY_NAMES_COPY = {
  en: {
    eyebrow: "Naam Karan",
    intro:
      "Find the perfect name for your child — filtered by birth star and scored by numerology compatibility.",
    badges: ["1400+ Names", "Instant Results", "100% Free"],
    modeLabel: "Baby name search mode",
    modes: { birth: "By Birth Details", nakshatra: "Select Nakshatra" },
    genderHeading: "Baby Gender",
    genderLabel: "Baby gender",
    genders: { all: "All", male: "Boy", female: "Girl" } as Record<string, string>,
    submit: "Find Baby Names",
    nakshatraLabel: "Select Nakshatra",
    nakshatraPlaceholder: "Choose nakshatra...",
    pada: "Pada",
    optional: "(optional)",
    padaLabel: "Nakshatra pada",
    all: "All",
    birthDate: "Baby's Birth Date",
    birthDateHint: "(for numerology scoring)",
    needExactBirth: "Enter an exact birth time and select the birth place from the suggestions.",
    needNakshatra: "Please select a nakshatra and enter birth date.",
    error: "Something went wrong",
    selectedNakshatra: "Selected Nakshatra",
    startingLetters: "Recommended Starting Letters",
    padaHighlighted: (pada: number | null | undefined) => `Pada ${pada} letter highlighted`,
    moonSign: "Moon sign",
    nakshatraWord: "Nakshatra",
    padaShort: (pada: number) => `pada ${pada}`,
    rashiName: (rashi: { name_en: string; name_hi: string; name_sa?: string }) =>
      rashi.name_sa ? `${rashi.name_sa} (${rashi.name_en})` : rashi.name_en,
    nakshatraName: (nakshatra: { name_en: string; name_hi: string }) =>
      nakshatra.name_en.replace(/_/g, " "),
    otherPadasHeading: (padas: readonly number[]) =>
      `Few names start with this pada's syllable — names from the other padas of the same nakshatra${
        padas.length ? ` (pada ${padas.join(", ")})` : ""
      }`,
    otherPadasNote:
      "Tradition also accepts the syllables of the birth nakshatra's other three padas.",
    mulank: "Mulank (Root)",
    bhagyank: "Bhagyank (Destiny)",
    count: (n: number) => `${n} names`,
    shortlisted: (n: number) => `${n} name${n !== 1 ? "s" : ""} shortlisted`,
    share: "Share with Family",
    empty:
      "No names found for this combination. Try changing the gender filter or selecting all padas.",
    again: "Search Again",
    letter: "Letter:",
    nameNumber: "Name No:",
    origins: {} as Record<string, string>,
    genderBadges: {} as Record<string, string>,
    rating: (score: number) => (score >= 9 ? "Excellent" : score >= 7 ? "Good" : "Fair"),
    added: "Added to shortlist",
    toggle: (name: string, selected: boolean) =>
      `${selected ? "Remove" : "Add"} ${name} ${selected ? "from" : "to"} shortlist`,
    shareMessage: (nakshatra: string, letters: string, mulank: number, bhagyank: number) =>
      `Baby Name Shortlist - ${nakshatra} Nakshatra\nStarting Letters: ${letters}\nMulank: ${mulank} | Bhagyank: ${bhagyank}`,
    scoreLabel: "Score",
    shareFooter: "Find more names:",
  },
  hi: {
    eyebrow: "नामकरण",
    intro:
      "अपने बच्चे के लिए सही नाम खोजें — जन्म नक्षत्र के अनुसार और अंक ज्योतिष के अंकों के साथ।",
    badges: ["1400+ नाम", "तुरंत परिणाम", "100% मुफ्त"],
    modeLabel: "नाम खोजने का तरीका",
    modes: { birth: "जन्म विवरण से", nakshatra: "नक्षत्र चुनें" },
    genderHeading: "बच्चे का लिंग",
    genderLabel: "बच्चे का लिंग",
    genders: { all: "सभी", male: "लड़का", female: "लड़की" } as Record<string, string>,
    submit: "बच्चों के नाम खोजें",
    nakshatraLabel: "नक्षत्र चुनें",
    nakshatraPlaceholder: "नक्षत्र चुनें...",
    pada: "चरण",
    optional: "(वैकल्पिक)",
    padaLabel: "नक्षत्र चरण",
    all: "सभी",
    birthDate: "बच्चे की जन्म तिथि",
    birthDateHint: "(अंक मिलान के लिए)",
    needExactBirth: "सटीक जन्म समय लिखें और सुझावों में से जन्म स्थान चुनें।",
    needNakshatra: "कृपया नक्षत्र चुनें और जन्म तिथि लिखें।",
    error: "नाम नहीं मिल सके। कृपया फिर से कोशिश करें।",
    selectedNakshatra: "चुना गया नक्षत्र",
    startingLetters: "शुभ आरंभिक अक्षर",
    padaHighlighted: (pada: number | null | undefined) => `चरण ${pada} का अक्षर हाइलाइट है`,
    moonSign: "चंद्र राशि",
    nakshatraWord: "नक्षत्र",
    padaShort: (pada: number) => `पद ${pada}`,
    rashiName: (rashi: { name_en: string; name_hi: string; name_sa?: string }) => rashi.name_hi,
    nakshatraName: (nakshatra: { name_en: string; name_hi: string }) => nakshatra.name_hi,
    otherPadasHeading: (padas: readonly number[]) =>
      `इस पद के नाम कम हैं — इसी नक्षत्र के अन्य पदों के नाम${
        padas.length ? ` (पद ${padas.join(", ")})` : ""
      }`,
    otherPadasNote: "परंपरा में जन्म नक्षत्र के अन्य तीन पदों के अक्षर भी स्वीकार्य हैं।",
    mulank: "मूलांक",
    bhagyank: "भाग्यांक",
    count: (n: number) => `${n} नाम`,
    shortlisted: (n: number) => `${n} नाम चुने गए`,
    share: "परिवार को भेजें",
    empty: "इस चयन के लिए कोई नाम नहीं मिला। लिंग फ़िल्टर बदलें या सभी चरण चुनें।",
    again: "फिर से खोजें",
    letter: "अक्षर:",
    nameNumber: "नामांक:",
    origins: { Sanskrit: "संस्कृत", Hindi: "हिंदी" } as Record<string, string>,
    genderBadges: { male: "लड़का", female: "लड़की", unisex: "दोनों" } as Record<string, string>,
    rating: (score: number) => (score >= 9 ? "उत्तम" : score >= 7 ? "अच्छा" : "सामान्य"),
    added: "सूची में जोड़ा गया",
    toggle: (name: string, selected: boolean) =>
      selected ? `${name} को सूची से हटाएँ` : `${name} को सूची में जोड़ें`,
    shareMessage: (nakshatra: string, letters: string, mulank: number, bhagyank: number) =>
      `बच्चों के नामों की सूची - ${nakshatra} नक्षत्र\nआरंभिक अक्षर: ${letters}\nमूलांक: ${mulank} | भाग्यांक: ${bhagyank}`,
    scoreLabel: "अंक",
    shareFooter: "और नाम देखें:",
  },
} as const;

export type BabyNamesCopy = (typeof BABY_NAMES_COPY)[BabyNamesLanguage];
